import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import {
  calculateDiscount,
  validateDiscountCodeSync,
} from "@/lib/discountCalculation";
import type { Tables } from "@/integrations/supabase/types";

// ---- Types ----

export type DiscountCodeRow = Tables<"discount_codes">;
export type DiscountCodeUseRow = Tables<"discount_code_uses">;

export interface DiscountCodeFormData {
  code: string;
  name?: string | null;
  description?: string | null;
  discount_type: string;
  discount_value: number;
  max_discount_amount?: number | null;
  applicable_to?: string[] | null;
  min_amount?: number | null;
  max_uses?: number | null;
  per_user_limit?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
  customer_category_ids?: string[] | null;
  specific_membership_type_ids?: string[] | null;
  specific_activity_type_ids?: string[] | null;
  specific_court_ids?: string[] | null;
  weekday_restriction?: number[] | null;
  time_from?: string | null;
  time_to?: string | null;
  tags?: string[] | null;
}

export interface DiscountResult {
  code: DiscountCodeRow;
  discountAmount: number;
}

export interface EdgeValidateContext {
  code: string;
  organization_id: string;
  usage_type: string;
  amount: number;
  booking_details?: {
    weekday?: number;
    time?: string;
    court_id?: string;
  };
  membership_type_id?: string;
  activity_type_id?: string;
}

export interface EdgeValidateResult {
  valid: boolean;
  discount_amount?: number;
  final_amount?: number;
  code_id?: string;
  reason?: string;
}

export interface EdgeApplyParams {
  code_id: string;
  user_id: string;
  organization_id: string;
  usage_type: string;
  reference_id: string;
  original_amount: number;
  discount_amount: number;
  final_amount: number;
  stripe_payment_intent_id?: string;
}

export interface CodeStats {
  totalUses: number;
  uniqueUsers: number;
  totalDiscountGiven: number;
  uses: DiscountCodeUseRow[];
}

// ---- Hook ----

export function useDiscountCodes(orgId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const queryKey = ["discount-codes", orgId];

  // Fetch all discount codes for org (admin view)
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("discount_codes")
        .select("*")
        .eq("organization_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DiscountCodeRow[];
    },
    enabled: !!orgId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["discount-codes"] });

  // Create a new discount code
  const createCode = useMutation({
    mutationFn: async (form: DiscountCodeFormData) => {
      // Sprint D Phase 4 dual-write: derive _minor + percent split from legacy form fields.
      // Mirrors trigger logic so the app is the source of truth (triggers are safety-net only).
      const isFixed = form.discount_type === "fixed" || form.discount_type === "fixed_amount";
      const isPercent = form.discount_type === "percentage" || form.discount_type === "percent";
      const discountValueMinor = isFixed && form.discount_value != null
        ? Math.round(form.discount_value * 100)
        : null;
      const discountPercent = isPercent && form.discount_value != null
        ? Math.round(form.discount_value)
        : null;
      const minAmountMinor = form.min_amount != null
        ? Math.round(form.min_amount * 100)
        : null;

      const { error } = await supabase.from("discount_codes").insert({
        organization_id: orgId!,
        code: form.code.toUpperCase().trim(),
        name: form.name ?? null,
        description: form.description ?? null,
        discount_type: form.discount_type,
        discount_value: form.discount_value,
        // Phase 4 split — see migration 20260425191846 trigger COMMENTs.
        discount_value_minor: discountValueMinor,
        discount_percent: discountPercent,
        currency: "SEK",
        max_discount_amount: form.max_discount_amount ?? null,
        applicable_to: form.applicable_to ?? null,
        min_amount: form.min_amount ?? null,
        min_amount_minor: minAmountMinor,
        max_uses: form.max_uses ?? null,
        per_user_limit: form.per_user_limit ?? 1,
        valid_from: form.valid_from ?? null,
        valid_until: form.valid_until ?? null,
        is_active: form.is_active ?? true,
        customer_category_ids: form.customer_category_ids ?? null,
        specific_membership_type_ids: form.specific_membership_type_ids ?? null,
        specific_activity_type_ids: form.specific_activity_type_ids ?? null,
        specific_court_ids: form.specific_court_ids ?? null,
        weekday_restriction: form.weekday_restriction ?? null,
        time_from: form.time_from ?? null,
        time_to: form.time_to ?? null,
        tags: form.tags ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Rabattkod skapad");
    },
    onError: (err: Error) => {
      if (err.message?.includes("duplicate") || err.message?.includes("unique")) {
        toast.error("En kod med detta namn finns redan");
      } else {
        toast.error(err.message || "Kunde inte skapa rabattkod");
      }
    },
  });

  // Update an existing discount code
  const updateCode = useMutation({
    mutationFn: async ({
      id,
      ...form
    }: Partial<DiscountCodeFormData> & { id: string }) => {
      const payload: Record<string, unknown> = {};
      if (form.code !== undefined) payload.code = form.code.toUpperCase().trim();
      if (form.name !== undefined) payload.name = form.name;
      if (form.description !== undefined) payload.description = form.description;
      if (form.discount_type !== undefined) payload.discount_type = form.discount_type;
      if (form.discount_value !== undefined) payload.discount_value = form.discount_value;
      // Sprint D Phase 4 dual-write: when discount_value or discount_type changes,
      // recompute _minor + percent split. Trigger is safety-net but app is source of truth.
      if (form.discount_value !== undefined || form.discount_type !== undefined) {
        // Need current type if only value changed. One round-trip for correctness.
        let effectiveType = form.discount_type;
        let effectiveValue = form.discount_value;
        if (effectiveType === undefined || effectiveValue === undefined) {
          const { data: existing } = await supabase
            .from("discount_codes")
            .select("discount_type, discount_value")
            .eq("id", id)
            .single();
          if (effectiveType === undefined) effectiveType = (existing as DiscountCodeRow | null)?.discount_type;
          if (effectiveValue === undefined) effectiveValue = (existing as DiscountCodeRow | null)?.discount_value ?? undefined;
        }
        const isFixed = effectiveType === "fixed" || effectiveType === "fixed_amount";
        const isPercent = effectiveType === "percentage" || effectiveType === "percent";
        payload.discount_value_minor = isFixed && effectiveValue != null
          ? Math.round(effectiveValue * 100) : null;
        payload.discount_percent = isPercent && effectiveValue != null
          ? Math.round(effectiveValue) : null;
      }
      if (form.max_discount_amount !== undefined) payload.max_discount_amount = form.max_discount_amount;
      if (form.applicable_to !== undefined) payload.applicable_to = form.applicable_to;
      if (form.min_amount !== undefined) {
        payload.min_amount = form.min_amount;
        payload.min_amount_minor = form.min_amount != null ? Math.round(form.min_amount * 100) : null;
      }
      if (form.max_uses !== undefined) payload.max_uses = form.max_uses;
      if (form.per_user_limit !== undefined) payload.per_user_limit = form.per_user_limit;
      if (form.valid_from !== undefined) payload.valid_from = form.valid_from;
      if (form.valid_until !== undefined) payload.valid_until = form.valid_until;
      if (form.is_active !== undefined) payload.is_active = form.is_active;
      if (form.customer_category_ids !== undefined) payload.customer_category_ids = form.customer_category_ids;
      if (form.specific_membership_type_ids !== undefined) payload.specific_membership_type_ids = form.specific_membership_type_ids;
      if (form.specific_activity_type_ids !== undefined) payload.specific_activity_type_ids = form.specific_activity_type_ids;
      if (form.specific_court_ids !== undefined) payload.specific_court_ids = form.specific_court_ids;
      if (form.weekday_restriction !== undefined) payload.weekday_restriction = form.weekday_restriction;
      if (form.time_from !== undefined) payload.time_from = form.time_from;
      if (form.time_to !== undefined) payload.time_to = form.time_to;
      if (form.tags !== undefined) payload.tags = form.tags;

      const { error } = await supabase
        .from("discount_codes")
        .update(payload)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Rabattkod uppdaterad");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte uppdatera rabattkod");
    },
  });

  // Deactivate a discount code
  const deactivateCode = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("discount_codes")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Rabattkod avaktiverad");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte avaktivera rabattkod");
    },
  });

  // Toggle active status
  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("discount_codes")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte ändra status");
    },
  });

  // Validate a code string: lookup + check rules + return discount amount
  const validateCode = async (
    codeStr: string,
    context: { totalAmount: number; applicableTo?: string }
  ): Promise<{ valid: boolean; discount?: DiscountResult; error?: string }> => {
    if (!orgId) return { valid: false, error: "Organisation saknas" };
    if (!user?.id) return { valid: false, error: "Du måste vara inloggad" };

    const trimmed = codeStr.trim().toUpperCase();
    if (!trimmed) return { valid: false, error: "Ange en kod" };

    // Lookup code in vouchers table (admin creates codes there via Kampanjkoder)
    const { data: voucherRow, error: lookupErr } = await supabase
      .from("vouchers")
      .select("*")
      .eq("organization_id", orgId)
      .eq("code", trimmed)
      .maybeSingle();

    if (lookupErr) return { valid: false, error: "Kunde inte kontrollera koden" };
    if (!voucherRow) return { valid: false, error: "Ogiltig kod" };

    // Map voucher row to DiscountCodeRow-compatible shape.
    // Sprint D Phase 4: spread carries discount_value_minor + discount_percent + currency
    // through to calculateDiscount, which prefers them over legacy discount_value.
    // Mapping examples:
    //   voucher{discount_type:'percent', discount_value:99, discount_percent:99, discount_value_minor:NULL}
    //     → row.discount_type='percentage' → calculateDiscount uses discount_percent=99 ✅
    //   voucher{discount_type:'fixed', discount_value:50, discount_value_minor:5000}
    //     → row.discount_type='fixed_amount' → calculateDiscount uses discount_value_minor/100=50 ✅
    const discountTypeMap: Record<string, string> = {
      fixed: "fixed_amount",
      percent: "percentage",
      free: "free",
    };

    const row = {
      ...voucherRow,
      discount_type: discountTypeMap[voucherRow.discount_type] ?? voucherRow.discount_type,
      per_user_limit: voucherRow.max_per_user,
      used_count: voucherRow.uses_count ?? 0,
      max_discount_amount: null,
      applicable_to: null,
      min_amount: null,
    } as unknown as DiscountCodeRow;

    const validation = validateDiscountCodeSync(row, {
      totalAmount: context.totalAmount,
      applicableTo: context.applicableTo,
      userUsageCount: 0, // Vouchers use global uses_count, not per-user tracking
    });

    if (!validation.valid) {
      return { valid: false, error: validation.error };
    }

    const discountAmount = calculateDiscount(row, context.totalAmount);

    return {
      valid: true,
      discount: { code: row, discountAmount },
    };
  };

  // Validate a code via the Edge Function (server-side validation with full restriction checks)
  const validateCodeViaEdge = async (
    context: EdgeValidateContext
  ): Promise<EdgeValidateResult> => {
    const { data, error } = await supabase.functions.invoke("validate-discount-code", {
      body: context,
    });

    if (error) {
      return { valid: false, reason: error.message || "Kunde inte validera koden" };
    }

    return data as EdgeValidateResult;
  };

  // Apply a code via the Edge Function (server-side with race condition protection)
  const applyCodeViaEdge = useMutation({
    mutationFn: async (params: EdgeApplyParams) => {
      const { data, error } = await supabase.functions.invoke("apply-discount-code", {
        body: params,
      });
      if (error) throw new Error(error.message || "Kunde inte tillämpa rabattkoden");
      if (!data?.success) throw new Error(data?.error || "Kunde inte tillämpa rabattkoden");
      return data;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err: Error) => {
      console.error("Failed to apply discount code via edge:", err);
      toast.error(err.message || "Kunde inte tillämpa rabattkoden");
    },
  });

  // Apply a code: increment used_count and insert discount_code_uses row (client-side fallback)
  const applyCode = useMutation({
    mutationFn: async ({
      discountCodeId,
      amountSaved,
      bookingId,
      eventId,
    }: {
      discountCodeId: string;
      amountSaved: number;
      bookingId?: string;
      eventId?: string;
    }) => {
      if (!user?.id) throw new Error("Ej inloggad");

      // Insert usage record.
      // Sprint D Phase 4 dual-write: amount_saved_minor + currency.
      // (original_amount/final_amount not available in this client-side fallback path
      //  — production flow uses applyCodeViaEdge which has full context.)
      const { error: useErr } = await supabase
        .from("discount_code_uses")
        .insert({
          discount_code_id: discountCodeId,
          user_id: user.id,
          amount_saved: amountSaved,
          amount_saved_minor: Math.round(amountSaved * 100),
          currency: "SEK",
          booking_id: bookingId ?? null,
          event_id: eventId ?? null,
          organization_id: orgId ?? null,
          discount_amount: amountSaved,
        });
      if (useErr) throw useErr;

      // Increment used_count on the code
      const { data: current } = await supabase
        .from("discount_codes")
        .select("used_count")
        .eq("id", discountCodeId)
        .single();

      const newCount = ((current as DiscountCodeRow | null)?.used_count ?? 0) + 1;
      const { error: updateErr } = await supabase
        .from("discount_codes")
        .update({ used_count: newCount })
        .eq("id", discountCodeId);
      if (updateErr) throw updateErr;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err: Error) => {
      console.error("Failed to apply discount code:", err);
      toast.error("Kunde inte tillämpa rabattkoden");
    },
  });

  // Fetch usage history for a specific code (admin)
  const useUsageHistory = (discountCodeId?: string) =>
    useQuery({
      queryKey: ["discount-code-uses", discountCodeId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("discount_code_uses")
          .select("*")
          .eq("discount_code_id", discountCodeId!)
          .order("used_at", { ascending: false });
        if (error) throw error;
        return (data ?? []) as DiscountCodeUseRow[];
      },
      enabled: !!discountCodeId,
    });

  // Stats sub-hook for a code: total uses, unique users, total discount given
  const useCodeStats = (codeId?: string) =>
    useQuery<CodeStats>({
      queryKey: ["discount-code-stats", codeId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("discount_code_uses")
          .select("*")
          .eq("discount_code_id", codeId!)
          .order("used_at", { ascending: false });
        if (error) throw error;

        const uses = (data ?? []) as DiscountCodeUseRow[];
        const uniqueUserIds = new Set(uses.map((u) => u.user_id));
        const totalDiscountGiven = uses.reduce(
          (sum, u) => sum + (u.discount_amount ?? u.amount_saved ?? 0),
          0
        );

        return {
          totalUses: uses.length,
          uniqueUsers: uniqueUserIds.size,
          totalDiscountGiven,
          uses,
        };
      },
      enabled: !!codeId,
    });

  return {
    codes: query.data ?? [],
    isLoading: query.isLoading,
    createCode,
    updateCode,
    deactivateCode,
    toggleActive,
    validateCode,
    validateCodeViaEdge,
    applyCode,
    applyCodeViaEdge,
    useUsageHistory,
    useCodeStats,
  };
}
